import styled, { keyframes, css } from "styled-components";
import { Link } from "react-router-dom";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.85); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 24px 0px rgba(31, 188, 112, 0.4); }
  50%       { box-shadow: 0 0 48px 8px rgba(31, 188, 112, 0.7); }
`;

/* ─── Page ─────────────────────────────────────────────────── */

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #0c0e0c;
  color: #f8fdf9;
`;

/* ─── Hero ─────────────────────────────────────────────────── */

export const Hero = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 72px 28px 60px;
  overflow: hidden;
`;

export const HeroGlow = styled.div`
  position: absolute;
  top: -80px;
  right: -80px;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(31, 188, 112, 0.25) 0%, transparent 70%);
  pointer-events: none;
`;

export const HeroGlow2 = styled.div`
  position: absolute;
  bottom: 40px;
  left: -60px;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(31, 188, 112, 0.12) 0%, transparent 70%);
  pointer-events: none;
`;

export const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background-color: rgba(31, 188, 112, 0.12);
  border: 1px solid rgba(31, 188, 112, 0.35);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #1fbc70;
  letter-spacing: 0.05em;
  margin-bottom: 28px;
  width: fit-content;
  animation: ${fadeUp} 0.5s ease both;
`;

export const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  background-color: #1fbc70;
  border-radius: 50%;
  animation: ${pulse} 1.6s ease-in-out infinite;
  display: inline-block;
`;

export const ServiceName = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 16px;
  animation: ${fadeUp} 0.5s 0.1s ease both;
`;

export const HeroTitle = styled.h1`
  font-size: clamp(40px, 10vw, 52px);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #f8fdf9;
  margin-bottom: 8px;
  animation: ${fadeUp} 0.5s 0.15s ease both;

  .accent {
    display: block;
    background: linear-gradient(90deg, #1fbc70, #a8f0cb, #1fbc70);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 3s linear infinite;
  }
`;

export const HeroSub = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  margin-top: 20px;
  margin-bottom: 44px;
  animation: ${fadeUp} 0.5s 0.2s ease both;
`;

export const CtaGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${fadeUp} 0.5s 0.25s ease both;
`;

export const PrimaryButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  background-color: #1fbc70;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  border-radius: 16px;
  text-decoration: none;
  letter-spacing: -0.01em;
  animation: ${glow} 3s ease-in-out infinite;
  transition: background-color 0.2s, transform 0.1s;

  &:active {
    transform: scale(0.98);
    background-color: #18a860;
  }
`;

export const SecondaryButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  background-color: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  font-weight: 600;
  border-radius: 16px;
  text-decoration: none;
  transition: background-color 0.2s, transform 0.1s;

  &:active {
    transform: scale(0.98);
    background-color: rgba(255, 255, 255, 0.12);
  }
`;

export const ScrollHint = styled.div`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  animation: ${fadeUp} 0.5s 0.4s ease both;
`;

export const ScrollLine = styled.div`
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, rgba(31, 188, 112, 0.8), transparent);
`;

export const ScrollLabel = styled.p`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

/* ─── Categories ────────────────────────────────────────────── */

export const Section = styled.section<{ light?: boolean }>`
  padding: 52px 24px;
  ${({ light }) =>
    light &&
    css`
      background-color: #f5faf7;
    `}
`;

export const Label = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #1fbc70;
  margin-bottom: 10px;
`;

export const SectionTitle = styled.h2<{ dark?: boolean }>`
  font-size: 24px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: -0.02em;
  margin-bottom: 28px;
  color: ${({ dark }) => (dark ? "#111" : "#f8fdf9")};
  word-break: keep-all;
`;

export const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  background-color: #161a16;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 18px;
  transition: background-color 0.15s;

  &:active {
    background-color: #1e251e;
  }
`;

export const CategoryIconBox = styled.div`
  width: 52px;
  height: 52px;
  background-color: rgba(31, 188, 112, 0.1);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }
`;

export const CategoryText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CategoryLabel = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: #f8fdf9;
  letter-spacing: -0.01em;
`;

export const CategorySub = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CategoryArrow = styled.span`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

/* ─── Features ──────────────────────────────────────────────── */

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 28px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);

  &:last-child {
    border-bottom: none;
  }
`;

export const FeatureNumber = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #1fbc70;
  letter-spacing: 0.05em;
  min-width: 24px;
  padding-top: 3px;
`;

export const FeatureContent = styled.div`
  flex: 1;
`;

export const FeatureTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: #111;
  margin-bottom: 6px;
  letter-spacing: -0.01em;
`;

export const FeatureDesc = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.7;
  word-break: keep-all;
`;

export const FeatureEmoji = styled.div`
  width: 48px;
  height: 48px;
  background-color: #f0fdf7;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`;

/* ─── CTA bottom ────────────────────────────────────────────── */

export const CtaSection = styled.section`
  padding: 52px 24px;
  background: linear-gradient(135deg, #0f2a1e 0%, #0c0e0c 100%);
  position: relative;
  overflow: hidden;
`;

export const CtaGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 260px;
  height: 260px;
  background: radial-gradient(circle, rgba(31, 188, 112, 0.18) 0%, transparent 70%);
  pointer-events: none;
`;

export const CtaTitle = styled.h2`
  font-size: 26px;
  font-weight: 900;
  color: #f8fdf9;
  line-height: 1.25;
  letter-spacing: -0.02em;
  margin-bottom: 12px;
  word-break: keep-all;
`;

export const CtaSub = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.7;
  margin-bottom: 32px;
  word-break: keep-all;
`;

/* ─── Footer ────────────────────────────────────────────────── */

export const Footer = styled.footer`
  padding: 28px 24px;
  background-color: #080a08;
  text-align: center;

  p {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.2);
    line-height: 2;
  }
`;
